import PropTypes from "prop-types";
import { useSelector } from "react-redux";

const ComparisonLegend = ({ legends = [], withBottomMargin = false, moduleName }) => {
    const productCategoryData = useSelector((state) => state.settingsStore.productCategory);
    const isHairCare = process.env?.REACT_APP_HAIR_CARE?.toLowerCase() === productCategoryData?.label?.toLowerCase();
    const isSkinCare = process.env?.REACT_APP_SKIN_CARE?.toLowerCase() === productCategoryData?.label?.toLowerCase();
    const isBeautyAndWellbeing = isHairCare || isSkinCare;

    const tTestConfidenceInterval = useSelector((state) => state.tTestStore.tTestconfidenceInterval);
    const tTestType = useSelector((state) => state.tTestStore.tTestType);

    const is90Confidence = Number(tTestConfidenceInterval) === 0.9;
    const isHairCareOnly = isHairCare && !isSkinCare;

    const isZTest = tTestType && (
        tTestType.toLowerCase() === "z test" ||
        tTestType.toLowerCase().includes("z-test") ||
        tTestType.toLowerCase().includes("z test")
    );

    const isStudentTTest = tTestType && (
        tTestType.toLowerCase() === "student's t test comparisons" ||
        tTestType.toLowerCase() === "student's t test comparisons(1-tailed)" ||
        tTestType.toLowerCase().includes("student's t test") ||
        tTestType.toLowerCase().includes("student t test")
    );

    const isZTestOrStudentTTest = isZTest || isStudentTTest;

    if (!Array.isArray(legends) || legends.length < 2) return null;


    // Determine which is user product and which is competitor
    const isCompArray = legends.map((item) => item.isCompetitorProduct);
    let ulP = null;
    let coP = null;

    if (isCompArray[0] === true && isCompArray[1] === false) {
        ulP = legends[1].title;
        coP = legends[0].title;
    } else {
        ulP = legends[0].title;
        coP = legends[1].title;
    }

    const containerStyle = {
        display: "flex",
        justifyContent: "center",
        gap: "35px",
        marginTop: "18px",
        ...(withBottomMargin && { marginBottom: "30px" }),
    };

    const legendBox = (color, text) => (
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <div style={{ height: "20px", width: "20px", backgroundColor: color }}></div>
            <p style={{
                fontSize: "12px",
                fontWeight: 600,
                margin: 0,
                width: isBeautyAndWellbeing ? "auto" : (moduleName === "productPreference" ? "auto" : "280px"),
            }}>{text}</p>
        </div>
    );

    if (moduleName === "fragranceMetrics") {
        return (
            <div style={containerStyle}>
                {legendBox("green", `${ulP} Significantly Stronger Fragrance than ${coP}`)}
                {legendBox("rgb(255, 191, 0)", `No Significant Difference in Fragrance between ${ulP} and ${coP}`)}
                {legendBox("red", `${ulP} Significantly Weaker Fragrance than ${coP}`)}
            </div>
        );
    }

    if (moduleName === "preference" || moduleName === "productPreference") {
        return (
            <div style={containerStyle}>
                {legendBox("green", `${ulP} Significantly More Preferred than ${coP}`)}
                {legendBox("rgb(255, 191, 0)", `No Significant Difference in Preference between ${ulP} and ${coP}`)}
                {legendBox("red", `${ulP} Significantly Less Preferred than ${coP}`)}
            </div>
        );
    }

    if (moduleName === "clt") {
        return (
            <div style={containerStyle}>
                {legendBox("green", `${ulP} Significantly Higher than ${coP}`)}
                {legendBox("rgb(255, 191, 0)", `${ulP} Parity with ${coP}`)}
                {legendBox("red", `${ulP} Significantly Lower than ${coP}`)}
            </div>
        );
    }

    if (isBeautyAndWellbeing) {
        if (isHairCareOnly && isZTestOrStudentTTest) {
            // 90% confidence interval legend
            if (is90Confidence) {
                return (
                    <div style={containerStyle}>
                        {legendBox("#B4FE92", "Directional higher than reference")}
                        {legendBox("#FFE390", "Parity to reference")}
                        {legendBox("#FF8484", "Directional lower than reference")}
                        {legendBox("#808080", "Reference product")}
                    </div>
                );
            }

            const is80OneTailed = Number(tTestConfidenceInterval) === 0.8;
            const is99Confidence = Number(tTestConfidenceInterval) === 0.99;

            // 80% (1-tailed) or 99% confidence interval legend 
            if (is80OneTailed || is99Confidence) {
                // Win/Parity/Loss colors:
                // Green (#008000) -> Win
                // Yellow (#FFC000) -> Parity
                // Red (#FF0000) -> Loss

                return (
                    <div style={containerStyle}>
                        {legendBox("#008000", "Win")}
                        {legendBox("#FFC000", "Parity")}
                        {legendBox("#FF0000", "Loss")}
                        {legendBox("#808080", "Reference product")}
                    </div>
                );
            }
        }

        return (
            <div style={containerStyle}>
                {legendBox("green", 'Significantly higher than reference')}
                {legendBox("rgb(255, 191, 0)", 'Parity to reference')}
                {legendBox("red", 'Significantly lower than reference')}
                {legendBox("#808080", 'Reference product')}
            </div>
        );
    }

    return (
        <div style={containerStyle}>
            {legendBox("green", `${ulP} significantly higher than ${coP}`)}
            {legendBox("rgb(255, 191, 0)", `${ulP} parity with ${coP}`)}
            {legendBox("red", `${ulP} significantly lower than ${coP}`)}
        </div>
    );
};

ComparisonLegend.propTypes = {
    legends: PropTypes.array,
    withBottomMargin: PropTypes.bool,
    moduleName: PropTypes.string,
};

export default ComparisonLegend;
